import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { PRINT_RATIOS, PRINT_RATIO_CONFIGS, type PrintRatio, type CropData, type ProductCategory } from '@/types';
import { Check, Smartphone, Monitor } from 'lucide-react';
import type { Area, Point } from 'react-easy-crop';
import {
  transformCropStatesToCropData,
  validateAllCropsComplete,
  countCompletedCrops,
} from '@/utils/cropTransformers';
import { VALIDATION_MESSAGES, REQUIRED_CROP_COUNT } from '@/constants/cropper';
import { useCropState } from '@/hooks/useCropState';
import { CropperHeader, CropperCanvas, CropperControls, CropperActions } from './cropper-parts';

interface CropperProps {
  masterImage: File;
  productCategory: ProductCategory;
  productTitle: string;
  onCropsComplete: (crops: CropData[]) => void;
}

export function Cropper({ masterImage, productCategory, productTitle, onCropsComplete }: CropperProps) {
  const [currentRatio, setCurrentRatio] = useState<PrintRatio>('2:3');
  const [imageUrl, setImageUrl] = useState<string>('');
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('portrait');

  const {
    cropStates,
    onCropChange: handleCropChange,
    onZoomChange: handleZoomChange,
    onCropComplete: handleCropComplete,
    resetCrop,
  } = useCropState();

  // Load image
  useEffect(() => {
    const url = URL.createObjectURL(masterImage);
    setImageUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [masterImage]);

  const currentState = cropStates[currentRatio];
  const config = PRINT_RATIO_CONFIGS[currentRatio];

  // Calculate aspect ratio based on orientation
  const aspectRatio = orientation === 'portrait'
    ? config.width / config.height
    : config.height / config.width;

  // Wrapper functions to pass currentRatio to hook callbacks
  const onCropChange = (crop: Point) => handleCropChange(currentRatio, crop);
  const onZoomChange = (zoom: number) => handleZoomChange(currentRatio, zoom);
  const onCropComplete = (_croppedArea: Area, croppedAreaPixels: Area) =>
    handleCropComplete(currentRatio, _croppedArea, croppedAreaPixels);
  const handleResetCrop = () => resetCrop(currentRatio);

  const handleComplete = () => {
    const completedCrops = transformCropStatesToCropData(cropStates, orientation);

    if (completedCrops.length !== REQUIRED_CROP_COUNT) {
      alert(VALIDATION_MESSAGES.INCOMPLETE_CROPS);
      return;
    }

    onCropsComplete(completedCrops);
  };

  const allCropsComplete = validateAllCropsComplete(cropStates);

  return (
    <div className="space-y-6">
      {/* Orientation Toggle */}
      <Card className="p-4">
        <Label className="text-sm font-medium mb-3 block">Orientation (applies to all ratios)</Label>
        <div className="flex gap-2">
          <Button
            variant={orientation === 'portrait' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setOrientation('portrait')}
            className="flex-1"
          >
            <Smartphone className="h-4 w-4 mr-2" />
            Portrait
          </Button>
          <Button
            variant={orientation === 'landscape' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setOrientation('landscape')}
            className="flex-1"
          >
            <Monitor className="h-4 w-4 mr-2" />
            Landscape
          </Button>
        </div>
      </Card>

      {/* Ratio Selector */}
      <Card className="p-4">
        <Label className="text-sm font-medium mb-3 block">Select Print Ratio</Label>
        <div className="grid grid-cols-6 gap-2">
          {PRINT_RATIOS.map((ratio) => (
            <Button
              key={ratio}
              variant={currentRatio === ratio ? 'default' : 'outline'}
              size="sm"
              onClick={() => setCurrentRatio(ratio)}
              className="relative"
            >
              {ratio}
              {cropStates[ratio].croppedAreaPixels && (
                <span className="absolute -top-1 -right-1 h-3 w-3 bg-green-500 rounded-full border-2 border-background flex items-center justify-center">
                  <Check className="h-2 w-2 text-white" />
                </span>
              )}
            </Button>
          ))}
        </div>
      </Card>

      {/* Crop Area */}
      <Card className="overflow-hidden">
        <CropperHeader
          config={config}
          orientation={orientation}
          onReset={handleResetCrop}
        />

        <CropperCanvas
          imageUrl={imageUrl}
          crop={currentState.crop}
          zoom={currentState.zoom}
          aspectRatio={aspectRatio}
          onCropChange={onCropChange}
          onZoomChange={onZoomChange}
          onCropComplete={onCropComplete}
        />

        <CropperControls
          zoom={currentState.zoom}
          onZoomChange={onZoomChange}
        />
      </Card>

      {/* Actions */}
      <CropperActions
        allCropsComplete={allCropsComplete}
        completedCount={countCompletedCrops(cropStates)}
        totalCount={REQUIRED_CROP_COUNT}
        onContinue={handleComplete}
      />
    </div>
  );
}
